import React, { useState, useEffect } from "react";
import { ShieldCheck, Loader2, CheckCircle, XCircle, Edit2, ChevronDown } from "lucide-react";
import axios from "axios";
import DoneByBadge from "./DoneByBatch";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const InlineBanner = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 mt-2 ${
      type === "success"
        ? "bg-green-50 text-green-700 border border-green-200"
        : "bg-red-50 text-red-700 border border-red-200"
    }`}>
      {type === "success" ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {msg}
    </div>
  );
};

const STATUS_CONFIG = {
  verified: {
    label: "Verified",
    icon: <CheckCircle className="h-3.5 w-3.5 text-green-600" />,
    pill: "bg-green-100 text-green-700 border border-green-200",
    dot: "bg-green-500",
    selectedBorder: "border-green-400 bg-green-50",
  },
  not_verified: {
    label: "Not Verified",
    icon: <XCircle className="h-3.5 w-3.5 text-red-500" />,
    pill: "bg-red-100 text-red-700 border border-red-200",
    dot: "bg-red-500",
    selectedBorder: "border-red-400 bg-red-50",
  },
};

const normaliseStatus = (raw) => {
  if (raw === "success" || raw === "verified") return "verified";
  if (raw === "failed" || raw === "rejected" || raw === "not_verified") return "not_verified";
  return null;
};

const NameVerificationSection = ({
  workerId,
  initialStatus = null,
  doneByName = null,
  doneAt = null,
  workerName = null,
  aadhaarName = null,
  bankName = null,
  initialIsBankNameException = false,
  initialBankExceptionReason = null,
  initialRejectionReason = null,
  initialAadhaarNameMatched = false,
  initialIsOverAgeException = false,
  initialOverAgeExceptionReason = null,
  initialIsRefCheckException = false,
  initialRefCheckExceptionReason = null,
  initialCompanyId = null, // latest company from attendance records (optional prefill)
}) => {
  const [status, setStatus]                 = useState(normaliseStatus(initialStatus));
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [isEditing, setIsEditing]           = useState(false);
  const [message, setMessage]               = useState({ text: "", type: "success" });
  const [localMeta, setLocalMeta]           = useState({ byName: doneByName, at: doneAt });
  const [selectedStatus, setSelectedStatus] = useState("");

  const [bankException, setBankException]       = useState(false);
  const [reason, setReason]                     = useState("");
  const [OverAgeException, setOverAgeException]       = useState(false);
  const [OverAgereason, setOverAgeReason]                     = useState("");
  const [refCheckException, setrefCheckException]       = useState(false);
  const [refCheckreason, setrefCheckReason]                     = useState("");

  // Optional company selection (all companies, regardless of location)
  const [companies, setCompanies]               = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId || "");

  // Independent checkbox — can combine with "verified"
  const [aadhaarNameMatched, setAadhaarNameMatched] = useState(false);
  // Stored after save — shown in view mode
  const [savedAadhaarNameMatched, setSavedAadhaarNameMatched] = useState(initialAadhaarNameMatched);

  const [localDetails, setLocalDetails] = useState({
    isBankNameException: initialIsBankNameException,
    bankExceptionReason: initialBankExceptionReason,
    rejectionReason: initialRejectionReason,
    isOverAgeException: initialIsOverAgeException,
    overAgeExceptionReason: initialOverAgeExceptionReason,
    isRefCheckException: initialIsRefCheckException,
    refCheckExceptionReason: initialRefCheckExceptionReason,
  });

  useEffect(() => {
    const norm = normaliseStatus(initialStatus);
    setStatus(norm);
    setSelectedStatus("");
    setLocalMeta({ byName: doneByName, at: doneAt });
    setLocalDetails({
      isBankNameException: initialIsBankNameException,
      bankExceptionReason: initialBankExceptionReason,
      rejectionReason: initialRejectionReason,
      isOverAgeException: initialIsOverAgeException,
      overAgeExceptionReason: initialOverAgeExceptionReason,
      isRefCheckException: initialIsRefCheckException,
      refCheckExceptionReason: initialRefCheckExceptionReason,
    });
    setSavedAadhaarNameMatched(initialAadhaarNameMatched);
    setIsEditing(false);
    setBankException(false);
    setReason("");
    setOverAgeException(false);
    setOverAgeReason("");
    setrefCheckException(false);
    setrefCheckReason("");
    setAadhaarNameMatched(false);
    setSelectedCompanyId(initialCompanyId || "");
  }, [
    initialStatus,
    doneByName,
    doneAt,
    initialIsBankNameException,
    initialBankExceptionReason,
    initialRejectionReason,
    initialAadhaarNameMatched,
    initialIsOverAgeException,
    initialOverAgeExceptionReason,
    initialIsRefCheckException,
    initialRefCheckExceptionReason,
    initialCompanyId,
  ]);

  // Fetch all companies (no location filter) the first time edit mode opens
  useEffect(() => {
    if (!isEditing || companies.length > 0) return;
    let cancelled = false;
    (async () => {
      setCompaniesLoading(true);
      try {
        const token = localStorage.getItem("superadmintoken");
        const res = await axios.get(`${BASE_URL}/OnDemandCompanies/list-companies/all`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          params: { isActive: false },
        });
        if (!cancelled && res?.data?.success && Array.isArray(res?.data?.companies)) {
          setCompanies(
            res.data.companies
              .map((item) => ({
                id: item._id || item.id,
                name: item.name || "",
                code: item.companyCode || "",
              }))
              .filter((c) => c.id && c.name)
          );
        }
      } catch (err) {
        if (!cancelled) flash("Failed to load companies", "error");
      } finally {
        if (!cancelled) setCompaniesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  useEffect(() => {
    if (!companies.length || !selectedCompanyId) return;
    if (!companies.some((c) => c.id === selectedCompanyId)) setSelectedCompanyId("");
  }, [companies, selectedCompanyId]);

  const flash = (msg, type = "success") => {
    setMessage({ text: msg, type });
    setTimeout(() => setMessage({ text: "", type: "success" }), 3500);
  };

const handleStatusSelect = (key) => {
  setSelectedStatus(key);
  setBankException(false);
  setReason("");
  setOverAgeException(false);
  setOverAgeReason("");
  setrefCheckException(false);
  setrefCheckReason("");
};

  const handleSubmit = async () => {
    if (!selectedStatus) { flash("Please select a status", "error"); return; }
    if (selectedStatus === "not_verified" && !reason.trim()) {
      flash("Please provide a reason for not verifying", "error"); return;
    }
    if (selectedStatus === "verified" && bankException && !reason.trim()) {
      flash("Please provide a reason for the bank name exception", "error"); return;
    }
    if (selectedStatus === "verified" && OverAgeException && !OverAgereason.trim()) {
      flash("Please provide a reason for the overage exception", "error"); return;
    }
    if (selectedStatus === "verified" && refCheckException && !refCheckreason.trim()) {
      flash("Please provide a reason for the ref check exception", "error"); return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("superadmintoken");
      const apiStatus = selectedStatus === "verified" ? "success" : "failed";

      const payload = {
        workerId,
        status: apiStatus,
        aadhaarNameMatched: aadhaarNameMatched,
        ...(selectedCompanyId && { companyId: selectedCompanyId }),
        ...(selectedStatus === "not_verified" && reason.trim() && { rejectionReason: reason.trim() }),
        ...(selectedStatus === "verified" && { bankNameException: bankException }),
        ...(selectedStatus === "verified" && bankException && reason.trim() && { bankExceptionReason: reason.trim() }),
        ...(selectedStatus === "verified" && { OverAgeException: OverAgeException }),
        ...(selectedStatus === "verified" && OverAgeException && OverAgereason.trim() && { OverAgeExceptionReason: OverAgereason.trim() }),
        ...(selectedStatus === "verified" && { RefCheckException: refCheckException }),
        ...(selectedStatus === "verified" && refCheckException && refCheckreason.trim() && { RefCheckExceptionReason: refCheckreason.trim() }),
      };

      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/update-name-verification`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        setStatus(selectedStatus);
        setSavedAadhaarNameMatched(selectedStatus === "verified" ? aadhaarNameMatched : false);
        setIsEditing(false);
        setBankException(false);
        setReason("");
        setOverAgeException(false);
        setOverAgeReason("");
        setrefCheckException(false);
        setrefCheckReason("");
        setSelectedStatus("");
        setAadhaarNameMatched(false);
        setSelectedCompanyId(initialCompanyId || "");
        const meta = response.data.data?.nameVerification;
        setLocalMeta({
          byName: meta?.doneByName || localMeta.byName,
          at: meta?.doneAt || new Date().toISOString(),
        });
        setLocalDetails(
          meta
            ? {
                isBankNameException: !!meta.isBankNameException,
                bankExceptionReason: meta.bankExceptionReason,
                rejectionReason: meta.rejectionReason,
                isOverAgeException: !!meta.isOverAgeException,
                overAgeExceptionReason: meta.overAgeExceptionReason,
                isRefCheckException: !!meta.isRefCheckException,
                refCheckExceptionReason: meta.refCheckExceptionReason,
              }
            : {
                isBankNameException: selectedStatus === "verified" ? bankException : false,
                bankExceptionReason: selectedStatus === "verified" && bankException ? reason.trim() : null,
                rejectionReason: selectedStatus === "not_verified" ? reason.trim() : null,
                isOverAgeException: selectedStatus === "verified" ? OverAgeException : false,
                overAgeExceptionReason: selectedStatus === "verified" && OverAgeException ? OverAgereason.trim() : null,
                isRefCheckException: selectedStatus === "verified" ? refCheckException : false,
                refCheckExceptionReason: selectedStatus === "verified" && refCheckException ? refCheckreason.trim() : null,
              }
        );
        flash(response.data.message || "Account verification updated.", "success");
      } else {
        throw new Error(response.data.message || "Failed to update");
      }
    } catch (err) {
      flash(err.response?.data?.message || err.message || "Failed to update.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditOpen = () => {
    setSelectedStatus("");
    setBankException(false);
    setReason("");
    setOverAgeException(false);
    setOverAgeReason("");
    setrefCheckException(false);
    setrefCheckReason("");
    setAadhaarNameMatched(false);
    setSelectedCompanyId(initialCompanyId || "");
    setMessage({ text: "", type: "success" });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedStatus("");
    setBankException(false);
    setReason("");
    setOverAgeException(false);
    setOverAgeReason("");
    setrefCheckException(false);
    setrefCheckReason("");
    setAadhaarNameMatched(false);
    setSelectedCompanyId(initialCompanyId || "");
    setMessage({ text: "", type: "success" });
  };

  const currentConfig = status ? STATUS_CONFIG[status] : null;

  const nameRows = [
    { label: "Profile Name", value: workerName },
    { label: "Aadhaar Name", value: aadhaarName },
    { label: "Bank Name",    value: bankName },
  ];

  return (
    <section className="p-4 rounded-lg border border-gray-300 bg-gray-800/5">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-300">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-gray-700" />
          <span className="text-sm font-semibold text-gray-800">Account Verification</span>
        </div>
        {!isEditing ? (
          <button
            onClick={handleEditOpen}
            className="flex items-center gap-0.5 text-[10px] text-indigo-500 hover:text-indigo-700 font-medium px-1.5 py-0.5 rounded hover:bg-indigo-50 border border-indigo-100"
          >
            <Edit2 className="h-2.5 w-2.5" /> {status ? "Edit" : "Add"}
          </button>
        ) : (
          <button
            onClick={handleCancel}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-0.5 rounded hover:bg-gray-200 transition"
          >
            Cancel
          </button>
        )}
      </div>

      {/* ── Names — always visible ── */}
      <div className="mb-3 space-y-1.5">
        {nameRows.map(({ label, value }) => (
          <div key={label} className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500 font-medium w-24 flex-shrink-0">{label}:</span>
            <span className={`text-[11px] font-semibold ${value ? "text-gray-800" : "text-gray-400"}`}>
              {value || "—"}
            </span>
          </div>
        ))}
      </div>

      {/* ── View mode ── */}
      {!isEditing && (
        <div className="p-3 rounded-lg border border-gray-300 bg-gray-100">
          {currentConfig ? (
            <div className="flex items-start gap-2">
              <div className="w-7 h-7 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                {currentConfig.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-xs font-semibold text-gray-800">Verification Status</p>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${currentConfig.pill}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${currentConfig.dot}`} />
                    {currentConfig.label}
                  </span>
                  {/* Aadhaar name matched tag */}
                  {savedAadhaarNameMatched && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                      <CheckCircle className="h-2.5 w-2.5" /> Aadhaar Name Matched
                    </span>
                  )}
                  {/* Bank name exception tag */}
                  {status === "verified" && localDetails.isBankNameException && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                      Bank Name Exception
                    </span>
                  )}
                  {/* OverAge exception tag */}
                  {status === "verified" && localDetails.isOverAgeException && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                      OverAge Exception
                    </span>
                  )}
                  {/* Ref Check exception tag */}
                  {status === "verified" && localDetails.isRefCheckException && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                      Ref Check Exception
                    </span>
                  )}
                </div>
                {status === "verified" && localDetails.isBankNameException && localDetails.bankExceptionReason && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    <span className="font-medium text-gray-600">Bank exception reason:</span> {localDetails.bankExceptionReason}
                  </p>
                )}
                {status === "verified" && localDetails.isOverAgeException && localDetails.overAgeExceptionReason && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    <span className="font-medium text-gray-600">OverAge exception reason:</span> {localDetails.overAgeExceptionReason}
                  </p>
                )}
                {status === "verified" && localDetails.isRefCheckException && localDetails.refCheckExceptionReason && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    <span className="font-medium text-gray-600">Ref check exception reason:</span> {localDetails.refCheckExceptionReason}
                  </p>
                )}
               {status === "not_verified" && localDetails.rejectionReason && (
  <p className="text-[10px] text-gray-500 mt-1 break-words whitespace-normal">
    <span className="font-medium text-gray-600">Reason:</span>{" "}
    {localDetails.rejectionReason}
  </p>
)}
                <DoneByBadge doneByName={localMeta.byName} doneAt={localMeta.at} label="Updated" />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-3.5 w-3.5 text-gray-400" />
              </div>
              <p className="text-xs text-gray-400 font-medium">Unchecked</p>
            </div>
          )}
        </div>
      )}

      {/* ── Edit mode ── */}
      {isEditing && (
        <div className="p-3 rounded-lg border border-gray-300 bg-gray-100 space-y-3">

          {/* ── Status pills ── */}
          <div className="flex items-center gap-2">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <label
                key={key}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border cursor-pointer transition-all text-xs font-medium ${
                  selectedStatus === key
                    ? cfg.selectedBorder
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  name={`name-ver-${workerId}`}
                  value={key}
                  checked={selectedStatus === key}
                  onChange={() => handleStatusSelect(key)}
                  className="hidden"
                  disabled={isSubmitting}
                />
                {cfg.icon}
                <span className="text-gray-700">{cfg.label}</span>
              </label>
            ))}
          </div>

          {/* Company dropdown (optional) — shown for both Verified and Not Verified */}
          {selectedStatus && (
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                Company <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  disabled={isSubmitting || companiesLoading}
                  className={`w-full px-2 pr-7 py-1.5 text-xs border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400 appearance-none disabled:bg-gray-50 ${
                    selectedCompanyId ? "text-gray-800" : "text-gray-400"
                  }`}
                >
                  <option value="" className="text-gray-400">
                    {companiesLoading ? "Loading companies..." : "Select company"}
                  </option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id} className="text-black">
                      {c.name}{c.code ? ` (${c.code})` : ""}
                    </option>
                  ))}
                </select>
                {companiesLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                ) : (
                  <ChevronDown className="h-3 w-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                )}
              </div>
            </div>
          )}

          {/* Extra fields for "verified" */}
          {selectedStatus === "verified" && (
            <div className="space-y-2">

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={aadhaarNameMatched}
                  onChange={(e) => setAadhaarNameMatched(e.target.checked)}
                  disabled={isSubmitting}
                  className="mt-0.5 accent-gray-700 h-3.5 w-3.5 flex-shrink-0"
                />
                <span className="text-[11px] text-gray-600 leading-snug">
                  Match profile name with aadhaar name
                  <span className="block text-[10px] text-gray-400">Check if you want to match profile name with aadhaar name.</span>
                </span>
              </label>

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bankException}
                  onChange={(e) => { setBankException(e.target.checked); setReason(""); }}
                  disabled={isSubmitting}
                  className="mt-0.5 accent-gray-700 h-3.5 w-3.5 flex-shrink-0"
                />
                <span className="text-[11px] text-gray-600 leading-snug">
                  Verified with bank name exception
                  <span className="block text-[10px] text-gray-400">Check if the bank name doesn't match but the worker is otherwise verified</span>
                </span>
              </label>
              {bankException && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                    Reason for exception <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for bank name exception..."
                    rows={2}
                    disabled={isSubmitting}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none disabled:bg-gray-50"
                  />
                </div>
              )}

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={OverAgeException}
                  onChange={(e) => { setOverAgeException(e.target.checked); setOverAgeReason(""); }}
                  disabled={isSubmitting}
                  className="mt-0.5 accent-gray-700 h-3.5 w-3.5 flex-shrink-0"
                />
                <span className="text-[11px] text-gray-600 leading-snug">
                  Verified with Overage exception
                  <span className="block text-[10px] text-gray-400">Check if the user is OverAge but the worker is otherwise verified</span>
                </span>
              </label>
              {OverAgeException && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                    Reason for exception <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={OverAgereason}
                    onChange={(e) => setOverAgeReason(e.target.value)}
                    placeholder="Enter reason for OverAge exception..."
                    rows={2}
                    disabled={isSubmitting}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none disabled:bg-gray-50"
                  />
                </div>
              )}

              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={refCheckException}
                  onChange={(e) => { setrefCheckException(e.target.checked); setrefCheckReason(""); }}
                  disabled={isSubmitting}
                  className="mt-0.5 accent-gray-700 h-3.5 w-3.5 flex-shrink-0"
                />
                <span className="text-[11px] text-gray-600 leading-snug">
                  Verified with Ref Check exception
                  <span className="block text-[10px] text-gray-400">Check if the refcheck is not verified but the worker is otherwise verified</span>
                </span>
              </label>
              {refCheckException && (
                <div>
                  <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
                    Reason for exception <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={refCheckreason}
                    onChange={(e) => setrefCheckReason(e.target.value)}
                    placeholder="Enter reason for Ref Check exception..."
                    rows={2}
                    disabled={isSubmitting}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none disabled:bg-gray-50"
                  />
                </div>
              )}
            </div>
          )}

          {/* Extra fields for "not_verified" */}
{selectedStatus === "not_verified" && (
  <div>
    <label className="block text-[11px] font-medium text-gray-600 mb-0.5">
      Reason for not verifying <span className="text-red-400">*</span>
    </label>

    <textarea
      value={reason}
      onChange={(e) => setReason(e.target.value)}
      placeholder="Enter reason for not verifying..."
      rows={2}
      maxLength={200}
      disabled={isSubmitting}
      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none disabled:bg-gray-50"
    />

    <div className="text-[10px] text-gray-400 text-right mt-0.5">
      {reason.length}/200
    </div>
  </div>
)}

          {/* Save button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedStatus}
              className={`px-3 py-1.5 text-xs bg-gray-800 text-white rounded-md hover:bg-gray-900 transition font-medium ${
                !selectedStatus || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Saving...</> : "Save"}
            </button>
          </div>
        </div>
      )}

      <InlineBanner msg={message.text} type={message.type} />
    </section>
  );
};

export default NameVerificationSection;